import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import { LESSON, WORKSHEET } from 'global/constants';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { useImageRatio } from 'approot/shared/images/image-ratio.hooks';
import { LessonWorksheetFragment_worksheet } from './sections/lesson-worksheets/__generated__/LessonWorksheetFragment';
import WorksheetCard from 'approot/shared/worksheet-card/worksheet-card';
import {
  EVENT_ACTION_EXPAND,
  EVENT_CATEGORY_LESSON,
  EVENT_CATEGORY_LESSON_NAV,
} from 'approot/shared/data-tracker/data-events.constants';
import { generateWorksheetDataAttributes } from './sheets.utils';
import { getSmallestPagePreviewUrl } from './lesson.utils';
import { NEXT, PREV, useActivityPreview } from './activity-preview.hooks';
import {
  ActivityCarouselButton,
  ActivityThumbnailButton,
  ActivityThumbnailScroll,
} from './activity-preview-shared';
import * as Styled from './activity-preview.style';
import { useAuthorisedAction } from 'approot/shared/hooks/authorised-action.hooks';
import { clickLink } from 'approot/shared/content-card/content-card.utils';
import { SimpleCardButton } from 'approot/shared/simple-card/simple-card';
import { ImagePicture } from 'approot/shared/images/images';

import blurredGreenCirclePng from './images/blurred-tile-green-circle.png';
import blurredGreenCircleWebp from './images/blurred-tile-green-circle.webp';
import blurredGreenCircleAvif from './images/blurred-tile-green-circle.avif';
import blurredGreenCirclePortraitPng from './images/blurred-tile-green-circle-portrait.png';
import blurredGreenCirclePortraitWebp from './images/blurred-tile-green-circle-portrait.webp';
import blurredGreenCirclePortraitAvif from './images/blurred-tile-green-circle-portrait.avif';

import blurredBlueSquarePng from './images/blurred-tile-blue-square.png';
import blurredBlueSquareWebp from './images/blurred-tile-blue-square.webp';
import blurredBlueSquareAvif from './images/blurred-tile-blue-square.avif';
import blurredBlueSquarePortraitPng from './images/blurred-tile-blue-square-portrait.png';
import blurredBlueSquarePortraitWebp from './images/blurred-tile-blue-square-portrait.webp';
import blurredBlueSquarePortraitAvif from './images/blurred-tile-blue-square-portrait.avif';

import blurredBlackDiamondPng from './images/blurred-tile-black-diamond.png';
import blurredBlackDiamondWebp from './images/blurred-tile-black-diamond.webp';
import blurredBlackDiamondAvif from './images/blurred-tile-black-diamond.avif';
import blurredBlackDiamondPortraitPng from './images/blurred-tile-black-diamond-portrait.png';
import blurredBlackDiamondPortraitWebp from './images/blurred-tile-black-diamond-portrait.webp';
import blurredBlackDiamondPortraitAvif from './images/blurred-tile-black-diamond-portrait.avif';

type Props = {
  contentId: number;
  worksheet: LessonWorksheetFragment_worksheet | null;
};

const LessonWorksheetPreviewGuest = ({ contentId, worksheet }: Props) => {
  const {
    thumbnailScrollRef,
    index,
    scrollPosition,
    setScrollPosition,
    handleMoveThumbnails,
    handleOnScroll,
    handleThumbnailSelect,
  } = useActivityPreview({
    initialIndex: 0,
  });

  const {
    regionBanner: { height },
  } = useRegionData();

  const { unauthorisedAccessHref } = useAuthorisedAction();
  const link = unauthorisedAccessHref();

  const navigate = useNavigate();

  const pages = worksheet?.document?.individualPages || [];
  const pageNumbers = pages.map((p, i) => i + 1);
  const currentSlide = pages[index];

  const imageProperties = useImageRatio(
    getSmallestPagePreviewUrl(worksheet?.document)
  );
  const isLandscape = imageProperties.isLandscape;
  const imageRatio = isLandscape ? imageProperties.ratio : 70;

  return (
    <Styled.ActivityContainer bannerHeight={height}>
      <Styled.ActivityCarousel>
        {currentSlide && (
          <WorksheetCard
            {...generateWorksheetDataAttributes(
              {
                id: contentId,
                type: LESSON,
              },
              EVENT_CATEGORY_LESSON,
              index + 1,
              pageNumbers,
              false
            )}
            //override download event
            dataEvent={EVENT_CATEGORY_LESSON_NAV}
            dataEventAction={EVENT_ACTION_EXPAND}
            dataEventLabel={{
              target_type: 'lesson-section',
              target_identifier: index,
              content_id: contentId,
              content_type: LESSON,
            }}
            isAlwaysViewable={false}
            useLazyLoad={false}
            isCardButtonRow
            isBookmarkable={false}
            canLink
            link={currentSlide.downloadURL || undefined}
            title={''}
            ariaLabel={`Page ${index + 1}`}
            images={
              worksheet?.document?.individualPages?.[index].pagesPreview || []
            }
            imageRatio={imageRatio}
            type={WORKSHEET}
          />
        )}

        {index !== 0 && (
          <ActivityCarouselButton
            onClick={() => {
              link && index > 2
                ? clickLink({ navigate, link })
                : handleThumbnailSelect(index - 1);
            }}
          />
        )}
        {index !== pages.length - 1 && (
          <ActivityCarouselButton
            isNext
            onClick={() => {
              link && index > 1
                ? clickLink({ navigate, link })
                : handleThumbnailSelect(index + 1);
            }}
          />
        )}
      </Styled.ActivityCarousel>

      <Styled.ActivityThumbnails>
        {!scrollPosition.start && (
          <ActivityThumbnailButton onClick={() => handleMoveThumbnails(PREV)} />
        )}
        {!scrollPosition.end && (
          <ActivityThumbnailButton
            isNext
            onClick={() => handleMoveThumbnails(NEXT)}
          />
        )}

        <ActivityThumbnailScroll
          ref={thumbnailScrollRef}
          handleOnScroll={handleOnScroll}
          setScrollPosition={setScrollPosition}
        >
          <Styled.ActivityThumbnailsWrapper>
            {pages?.map((page, pageIndex, array) => {
              const isLastItem = pageIndex === array.length - 1;
              const isSecondLastItem = pageIndex === array.length - 2;

              return (
                <Fragment key={pageIndex}>
                  {pageIndex < 3 ? (
                    <Styled.ActivityWorksheetThumbnail
                      id={`activity-${pageIndex}`}
                    >
                      <WorksheetCard
                        {...generateWorksheetDataAttributes(
                          {
                            id: contentId,
                            type: LESSON,
                          },
                          EVENT_CATEGORY_LESSON,
                          pageIndex + 1,
                          pageNumbers,
                          false
                        )}
                        isAlwaysViewable
                        useLazyLoad={false}
                        isBookmarkable={false}
                        isCardButtonRow
                        hideLinkElement
                        link={page.downloadURL || undefined}
                        isSelected={pageIndex === index}
                        title={''}
                        ariaLabel={`Page ${pageIndex + 1}`}
                        images={page.pagesPreview}
                        imageRatio={imageRatio}
                        type={WORKSHEET}
                        onClick={() => handleThumbnailSelect(pageIndex)}
                      />
                    </Styled.ActivityWorksheetThumbnail>
                  ) : (
                    <Styled.ActivityWorksheetThumbnail
                      id={`activity-${pageIndex}`}
                    >
                      <SimpleCardButton
                        ariaTitle={`Page ${pageIndex + 1}`}
                        onClick={() => {
                          link
                            ? clickLink({ navigate, link })
                            : handleThumbnailSelect(pageIndex);
                        }}
                        imageRatio={imageRatio}
                        isSelected={pageIndex === index}
                        bodyText={
                          <>
                            {!isLastItem && !isSecondLastItem && (
                              <ImagePicture
                                avif={{
                                  path: isLandscape
                                    ? blurredGreenCircleAvif
                                    : blurredGreenCirclePortraitAvif,
                                }}
                                webp={{
                                  path: isLandscape
                                    ? blurredGreenCircleWebp
                                    : blurredGreenCirclePortraitWebp,
                                }}
                                png={{
                                  path: isLandscape
                                    ? blurredGreenCirclePng
                                    : blurredGreenCirclePortraitPng,
                                }}
                                altText="Locked Green Circle activity. Sign in or reactivate to see all content."
                              />
                            )}

                            {isSecondLastItem && (
                              <ImagePicture
                                avif={{
                                  path: isLandscape
                                    ? blurredBlueSquareAvif
                                    : blurredBlueSquarePortraitAvif,
                                }}
                                webp={{
                                  path: isLandscape
                                    ? blurredBlueSquareWebp
                                    : blurredBlueSquarePortraitWebp,
                                }}
                                png={{
                                  path: isLandscape
                                    ? blurredBlueSquarePng
                                    : blurredBlueSquarePortraitPng,
                                }}
                                altText="Locked Blue Square activity. Sign in or reactivate to see all content."
                              />
                            )}

                            {isLastItem && (
                              <ImagePicture
                                avif={{
                                  path: isLandscape
                                    ? blurredBlackDiamondAvif
                                    : blurredBlackDiamondPortraitAvif,
                                }}
                                webp={{
                                  path: isLandscape
                                    ? blurredBlackDiamondWebp
                                    : blurredBlackDiamondPortraitWebp,
                                }}
                                png={{
                                  path: isLandscape
                                    ? blurredBlackDiamondPng
                                    : blurredBlackDiamondPortraitPng,
                                }}
                                altText="Locked Black Diamond activity. Sign in or reactivate to see all content."
                              />
                            )}
                          </>
                        }
                      />
                    </Styled.ActivityWorksheetThumbnail>
                  )}
                </Fragment>
              );
            })}
          </Styled.ActivityThumbnailsWrapper>
        </ActivityThumbnailScroll>
      </Styled.ActivityThumbnails>
    </Styled.ActivityContainer>
  );
};

export default LessonWorksheetPreviewGuest;
